import { getExpandedRowModel } from '@tanstack/react-table';
import clsx from 'clsx';
import { debounce } from 'lodash-es';
import {
  forwardRef,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  type Ref,
} from 'react';
import useResizeObserver, { type ObservedSize } from 'use-resize-observer';

import { Grid } from '@dynatrace/strato-components/layouts';

import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useDataTableV2ConfigElement } from '../hooks/useDataTableV2Config.js';
import { DataTableV2DefaultCell } from './components/DataTableV2DefaultCell.js';
import { DataTableV2EmptyState } from './components/DataTableV2EmptyState.js';
import { DataTableV2VirtualizationContainer } from './components/DataTableV2VirtualizationContainer.js';
import { DataTableV2SelectedRowsActions } from './components/TableActions/DataTableV2SelectedRowsActions.js';
import { DataTableV2SelectionChip } from './components/TableActions/DataTableV2SelectionChip.js';
import { DataTableV2TableActions } from './components/TableActions/DataTableV2TableActions.js';
import { DataTableV2TableActionsWrapper } from './components/TableActions/DataTableV2TableActionsWrapper.js';
import { DataTableV2ColumnOrderSettings } from './components/Toolbar/ColumnSettings/DataTableV2ColumnOrderSettings.js';
import {
  DataTableV2ColumnSettingsModal,
  DataTableV2ColumnSettingsModalRef,
} from './components/Toolbar/ColumnSettings/DataTableV2ColumnSettingsModal.js';
import { DataTableV2ColumnSettingsTrigger } from './components/Toolbar/ColumnSettings/DataTableV2ColumnSettingsTrigger.js';
import { DataTableV2ColumnVisibilitySettings } from './components/Toolbar/ColumnSettings/DataTableV2ColumnVisibilitySettings.js';
import {
  DataTableV2Toolbar,
  getToolbarConfig,
} from './components/Toolbar/DataTableV2Toolbar.js';
import {
  DataTableV2ColumnSettingsContextProvider,
  DataTableV2ContextProvider,
} from './contexts/DataTableV2Context.js';
import { getCoreRowModel } from './core-row-model.js';
import {
  dataTableV2CSS,
  dataTableV2FullWidthCSS,
  dataTableV2NotResizingCSS,
} from './DataTableV2.sty.js';
import { useColumnFontStyle } from './features/ColumnFontStyle.js';
import { useColumnOrder } from './features/ColumnOrder/ColumnOrder.js';
import { useColumnPinning } from './features/ColumnPinning/ColumnPinning.js';
import { useColumnSizing } from './features/ColumnSizing.js';
import { useColumnVisibility } from './features/ColumnVisibility/ColumnVisibility.js';
import { useDataTableV2ConfigRef } from './features/Config.js';
import { _getDevToolsSlot, DataTableV2DevTools } from './features/DevTools.js';
import {
  DataTableV2Download,
  useDownloadData,
} from './features/Download/Download.js';
import { useGanttChart } from './features/GanttChart/GanttChart.js';
import { useLayout } from './features/Layout.js';
import {
  DataTableV2GlobalLineWrapAction,
  useColumnLineWrap,
} from './features/LineWrap.js';
import { useLoadingState } from './features/LoadingState.js';
import { useMeterBarChartMaxValuesCalc } from './features/MeterBarChart/MeterBarChart.js';
import {
  DataTableV2Pagination,
  usePagination,
} from './features/Pagination/Pagination.js';
import { DataTableV2PaginationInternal } from './features/Pagination/PaginationInternal.js';
import {
  DataTableV2ExpandableRowTemplate,
  DataTableV2ExpandableRowWrapper,
  useRowDetails,
} from './features/RowDetails/RowDetails.js';
import { useRowIdentification } from './features/RowIdentification/RowIdentification.js';
import { useRowInteractivity } from './features/RowInteractivity/RowInteractivity.js';
import { useRowOrder } from './features/RowOrder/RowOrder.js';
import { useRowSelection } from './features/RowSelection/RowSelection.js';
import { getSortedRowModel } from './features/Sorting/sorted-row-model.js';
import { useSorting } from './features/Sorting/Sorting.js';
import { useSubRows } from './features/SubRows/SubRows.js';
import {
  dataTableV2HasPillThresholdsCSS,
  dataTableV2HasThresholdsCSS,
} from './features/Thresholds/Thresholds.sty.js';
import { useThresholds } from './features/Thresholds/Thresholds.js';
import {
  DataTableV2CellActions,
  DataTableV2ColumnActions,
  DataTableV2RowActions,
  useUserActions,
} from './features/UserActions/UserActions.js';
import { useVisualVariant } from './features/VisualVariants.js';
import { useBuiltInColumns } from './hooks/useBuiltInColumns.js';
import { useColumns } from './hooks/useColumns.js';
import { useDataTableV2Slots } from './hooks/useDataTableV2Slots.js';
import type { CoreOptions } from './hooks/useTable/createTable.js';
import type { TableOptions } from './hooks/useTable/types.js';
import { useTable } from './hooks/useTable/useTable.js';
import { type DataTableV2Props, type DataTableV2Ref } from './public.api.js';
import { Intent as CoreIntent } from '../../core/slots/Intent/Intent.js';

function InternalDataTable<TData, TValue>(
  props: PropsWithChildren<DataTableV2Props<TData>>,
  forwardedRef: Ref<DataTableV2Ref>,
) {
  const {
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    style: consumerStyle,
  } = props;

  // Hook used to adapt column definition for internal usage
  const [columns, consumerColumnDefMap] = useColumns<TData, TValue>(
    props.columns,
  );

  const configFullWidthOption = useDataTableV2ConfigElement('fullWidth');
  const fullWidth = props.fullWidth ?? configFullWidthOption;

  const configFullHeightOption = useDataTableV2ConfigElement('fullHeight');
  const fullHeight = props.fullHeight ?? configFullHeightOption;

  const options: TableOptions<TData> = {
    columns,
    data: props.data,
    // In order to keep the types in our own realm, the cast to align with our
    // CoreOptions is necessary.
    getCoreRowModel:
      getCoreRowModel() as unknown as CoreOptions<TData>['getCoreRowModel'],
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  };

  useRowIdentification(props, options);
  useLayout(props, options);
  useColumnLineWrap(props, options);
  useColumnFontStyle(props, options);
  useThresholds(props, options);
  useVisualVariant(props, options);
  useUserActions<TData>(props, options, consumerColumnDefMap);
  usePagination(props, options);
  useLoadingState(props, options);
  useColumnSizing(props, options);
  useSorting(props, options);
  useColumnVisibility(props, options);
  useColumnOrder(props, options);
  useRowSelection(props, options);
  useSubRows(props, options);
  useRowDetails(props, options);
  useRowInteractivity(props, options);
  useDownloadData(props, options);
  useGanttChart<TData, TValue>(props, options);
  useMeterBarChartMaxValuesCalc(props, options);
  useRowOrder(props, options);
  useColumnPinning(options);

  // Adds the builtin columns depending on which features are enabled
  options.columns = useBuiltInColumns(options);
  const table = useTable<TData>(options);

  // Gets the passed ReactTableDevtools in order to make debugging easier.
  const DevTools = _getDevToolsSlot(props, table);

  const columnSettingsModalRef =
    useRef<DataTableV2ColumnSettingsModalRef>(null);
  const tableRef = useDataTableV2ConfigRef(
    forwardedRef,
    columnSettingsModalRef,
    table,
    props,
  );
  const rootRef = useRef<HTMLDivElement | null>(null);
  const mergedRefs = useMergeRefs([rootRef, tableRef]);

  // Keep track of the root container size. We are updating a reference to
  // the container size with the actual size.
  // The `table.remeasureRow` is a memoized function, which will only run if
  // it's parameters change.
  const rootContainerSize = useRef<number>(
    Math.round(rootRef.current?.getBoundingClientRect().width ?? 0),
  );

  // Does not like being passed a debounce function.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onContainerResize = useCallback(
    // Debounce the resizing to avoid flickering and performance impacts
    // during resize. The rows are measured once the DataTableV2 width stabilizes
    // for a given number of milliseconds.
    debounce(
      (size: ObservedSize) => {
        if (size.width) {
          rootContainerSize.current = Math.round(size.width);
          table.remeasureRow(rootContainerSize.current);
        }
      },
      125,
      {},
    ),
    [table],
  );
  // Track the resizing of the DataTableV2 root container.
  useResizeObserver({
    ref: rootRef.current,
    onResize: onContainerResize,
  });
  // When the table re-renders, invoke the measureRow with the last known
  // value for the containerSize in order to keep the memoization stable.
  table.remeasureRow(rootContainerSize.current);

  const slots = useDataTableV2Slots(props.children);

  const { children: toolbarContent, ...restToolbarProps } =
    slots.toolbarProps || {};

  table.updatePageIndexOnEmptyPage();

  const { slots: toolbarSlots, toolbarItemProps } = useMemo(
    () => getToolbarConfig(toolbarContent),
    [toolbarContent],
  );

  return (
    <>
      <div
        ref={mergedRefs}
        className={clsx(
          dataTableV2CSS,
          {
            [dataTableV2FullWidthCSS]: fullWidth,
            [dataTableV2NotResizingCSS]:
              table.getState().columnSizingInfo.isResizingColumn === false,
          },
          {
            [dataTableV2HasThresholdsCSS]: table.options.hasThresholds,
            [dataTableV2HasPillThresholdsCSS]:
              table.options.hasThresholds &&
              table.options.rowThresholds?.some(
                (threshold) => threshold.type === 'pill',
              ),
          },
          props.className,
        )}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
      >
        <DataTableV2ContextProvider value={table}>
          {(toolbarContent ||
            slots.tableActions ||
            slots.selectedRowsActions) && (
            <Grid
              gridTemplateColumns="1fr auto"
              paddingBottom={4}
              gap={toolbarContent ? 8 : 0}
            >
              <DataTableV2TableActionsWrapper
                tableActions={slots.tableActions}
                selectedRowsActions={slots.selectedRowsActions}
              />
              {/* We need to actually render the toolbar here to trigger rerenders once the table context updates */}
              <DataTableV2ColumnSettingsContextProvider
                value={{
                  openColumnSettings:
                    columnSettingsModalRef.current?.openColumnSettings,
                }}
              >
                <DataTableV2Toolbar {...restToolbarProps}>
                  {toolbarContent}
                </DataTableV2Toolbar>
              </DataTableV2ColumnSettingsContextProvider>
            </Grid>
          )}
          <DataTableV2ColumnSettingsModal
            ref={columnSettingsModalRef}
            visibilitySettings={toolbarSlots.visibilitySettings}
            columnOrderSettings={toolbarSlots.columnOrderSettings}
            resetColumnVisibility={toolbarItemProps.resetColumnVisibility}
            resetColumnOrder={toolbarItemProps.resetColumnOrder}
          />

          <DataTableV2VirtualizationContainer
            table={table}
            emptyState={slots.emptyState}
            fullHeight={fullHeight ?? false}
          />

          {table.options.enablePagination === true ? (
            <DataTableV2PaginationInternal
              id={slots.pagination?.props.id}
              data-testid={slots.pagination?.props['data-testid']}
              pageSizeOptions={table.options.availablePaginationOptions ?? []}
              pageSize={table.getState().pagination.pageSize}
              onPageSizeChange={table.setPageSize}
              pageCount={table.getPageCount()}
              pageIndex={table.getState().pagination.pageIndex}
              onPageIndexChange={table.setPageIndex}
              enablePreviousPage={
                table.getState().pagination.enablePreviousPage
              }
              enableNextPage={table.getState().pagination.enableNextPage}
              totalRowsCount={table.getState().pagination.totalRowsCount}
            />
          ) : null}
        </DataTableV2ContextProvider>
      </div>
      {DevTools}
    </>
  );
}

InternalDataTable.displayName = 'DataTableV2';

const DataTableV2Fwd = /* @__PURE__ */ forwardRef(InternalDataTable);

/**
 * The `DataTableV2` is a component for building tables which organize information
 * into rows and columns.
 * @public
 */
export const DataTableV2 = Object.assign(DataTableV2Fwd, {
  Pagination: DataTableV2Pagination,
  ColumnActions: DataTableV2ColumnActions,
  CellActions: DataTableV2CellActions,
  RowActions: DataTableV2RowActions,
  SelectedRowsActions: DataTableV2SelectedRowsActions,
  DefaultCell: DataTableV2DefaultCell,
  EmptyState: DataTableV2EmptyState,
  TableActions: DataTableV2TableActions,
  DevTools: DataTableV2DevTools,
  Toolbar: DataTableV2Toolbar,
  VisibilitySettings: DataTableV2ColumnVisibilitySettings,
  ColumnOrderSettings: DataTableV2ColumnOrderSettings,
  ColumnSettingsTrigger: DataTableV2ColumnSettingsTrigger,
  SelectionChip: DataTableV2SelectionChip,
  DownloadData: DataTableV2Download,
  Intent: CoreIntent,
  ExpandableRow: DataTableV2ExpandableRowTemplate,
  ExpandableRowWrapper: DataTableV2ExpandableRowWrapper,
  LineWrap: DataTableV2GlobalLineWrapAction,
});
